import {
    AfterViewInit,
    Component,
    ElementRef,
    Inject,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    CMPS_DIALOG_DATA,
    CmpsConfirmDialogComponent,
    CmpsConfirmDialogOptions,
    CmpsDialog,
    CmpsDialogRef,
} from '@cmps/ng-uikit';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { delay, tap } from 'rxjs/operators';

import { AuthService } from '../../auth/auth.service';
import { navigateToExistingCommitment } from '../../coaching-detail/navigate';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { StateToProps } from '../../store';
import { disuseStore, useDispatcher, useStore } from '../../store.decorator';

import { DialogLayoutService } from './dialog-layout.service';
import { TranslationKeyMap } from './focus-selector.i18n';
import { focusSelectorReducer } from './focus-selector.reducer';
import { FocusSelectorState } from './focus-selector.state';
import { GridSortClickEvent } from './grid/reusable-grid.model';
import {
    FocusHistorySelectRowEvent,
    FocusHistorySortGridEvent,
} from './model/focus-history-event.model';
import FocusHistoryNavigationRefs from './model/focus-history-navigation-tab-refs.enum';
import { FocusSelectorDialogData } from './model/focus-selector-dialog-data.model';
import { FocusSelectorDialogResult } from './model/focus-selector-dialog-result.model';
import { FocusSelectorFocusModel } from './model/focus-selector-focus.model';
import { FocusSelectorGoalModel } from './model/focus-selector-goal.model';
import NavigationTabRefs from './model/focus-selector-navigation-tab-refs.enum';
import { FocusSelectorSelectFocusEvent } from './model/focus-selector-select-focus-event.model';
import { FocusSelectorTriggerSource } from './model/focus-selector-trigger-source.enum';
import { getFocusSelectorResult } from './selection-result/focus-selection-result.function';
import { focusSelectorSelectActiveForPerformerRow } from './state/active-for-performer-select.action';
import { focusSelectorSortActiveForPerformerGrid } from './state/active-for-performer-sort.action';
import { focusSelectorSelectActiveForTeamRow } from './state/active-for-team-select.action';
import { focusSelectorSortActiveForTeamGrid } from './state/active-for-team-sort.action';
import { focusSelectorSelectClosedForPerformerRow } from './state/closed-for-performer-select.action';
import { focusSelectorSortClosedForPerformerGrid } from './state/closed-for-performer-sort.action';
import { focusSelectorSelectClosedForTeamRow } from './state/closed-for-team-select.action';
import { focusSelectorSortClosedForTeamGrid } from './state/closed-for-team-sort.action';
import { focusSelectorFilterFocuses } from './state/focus-selection-filter.action';
import { focusSelectorSortFocuses } from './state/focus-selection-sort.action';
import {
    focusSelectorSelectFocus,
    focusSelectorSelectSubBehaviourRow,
} from './state/focus-selection.action';
import { focusSelectorInit } from './state/init.action';
import {
    FocusSelectorSelectGoalRow,
    focusSelectorSelectGoalRow,
} from './state/load-goals-select.action';
import {
    focusSelectorUpdateSelectedGoal,
    FocusSelectorUpdateSelectedGoal,
} from './state/load-goals-update.action';
import {
    focusSelectorSelectActiveMetric,
    focusSelectorSelectMetricRow,
} from './state/metric-selection.action';
import { focusSelectorSortMetrics } from './state/metric-sort.action';

@Component({
    selector: 'cmps-coaching-focus-selector',
    templateUrl: './focus-selector.component.html',
    host: {
        class: 'cmps-coaching-focus-selector',
        'data-e2e': 'component-focus-selector',
    },
})
@TakeUntilDestroy()
export class FocusSelectorComponent
    implements
        OnInit,
        AfterViewInit,
        OnDestroy,
        StateToProps<FocusSelectorState>
{
    state: FocusSelectorState;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private  */ _NavigationTabRefs = NavigationTabRefs;
    /** @private */ _TriggerSource = FocusSelectorTriggerSource;
    /** @private */ _activeTab: NavigationTabRefs;
    /** @private */ _forceColumn: boolean;

    constructor(
        /** @private */ @Inject(CMPS_DIALOG_DATA)
        readonly _options: FocusSelectorDialogData,
        private readonly _authService: AuthService,
        private readonly _el: ElementRef<HTMLElement>,
        private readonly _dialogLayoutService: DialogLayoutService,
        private readonly _dialog: CmpsDialog,
        private readonly _translateService: TranslateService,
        private readonly _router: Router,
        private readonly _activatedRoute: ActivatedRoute,
        private readonly _dialogRef: CmpsDialogRef<
            FocusSelectorComponent,
            FocusSelectorDialogResult
        >,
    ) {
        this._forceColumn = false;
    }

    ngOnInit(): void {
        useStore(
            this,
            `${this._options.storePathPrefix}.focusSelector`,
            focusSelectorReducer,
        );

        useDispatcher(
            this,
            focusSelectorInit({
                coachId: this._authService.user.id,
                performerId: this._options.performerId,
                activeMetricId: this._options.activeMetricId,
                selectedFocus: this._options.selected,
                goalsCollection: this._options.goalsCollection,
                triggerSource: this._options.triggerSource,
                commitmentId: this._options.commitmentId,
            }),
        );

        this._dialogLayoutService.forceColumn
            .pipe(takeUntilDestroy(this))
            .subscribe((forceColumn) => {
                this._forceColumn = forceColumn;
            });

        this._activeTab =
            this._options.triggerSource ===
            FocusSelectorTriggerSource.MANAGE_GOALS
                ? NavigationTabRefs.LOAD_GOALS
                : this._options.activeMetricId !== null
                ? NavigationTabRefs.FOCUS_SELECTION
                : NavigationTabRefs.METRIC_SELECTION;
    }

    ngAfterViewInit(): void {
        this._dialogLayoutService.observeParent(
            this._el.nativeElement.querySelector('cmps-dialog'),
        );
    }

    ngOnDestroy(): void {
        disuseStore(this);
    }

    /** @private */
    _navigateToTab(tabRef: NavigationTabRefs): void {
        this._activeTab = tabRef;
    }

    /** @private */
    _selectMetric($event: number): void {
        useDispatcher(
            this,
            focusSelectorSelectActiveMetric({
                activeMetricId: $event,
            }),
        );
        this._activeTab = NavigationTabRefs.FOCUS_SELECTION;
    }

    /** @private */
    _selectMetricRow($event: number): void {
        useDispatcher(
            this,
            focusSelectorSelectMetricRow({ metricRowId: $event }),
        );
    }

    /** @private */
    _sortMetricGrid($event: GridSortClickEvent): void {
        useDispatcher(
            this,
            focusSelectorSortMetrics({ column: $event.column }),
        );
    }

    /** @private */
    _selectSubBehaviourRow($event: number): void {
        useDispatcher(
            this,
            focusSelectorSelectSubBehaviourRow({ subBehaviourId: $event }),
        );
    }

    /** @private */
    _sortFocusSelectionGrid($event: GridSortClickEvent): void {
        useDispatcher(
            this,
            focusSelectorSortFocuses({ column: $event.column }),
        );
    }

    /** @private */
    _filterFocusSelectionGrid($event: number): void {
        useDispatcher(
            this,
            focusSelectorFilterFocuses({ behaviourId: $event }),
        );
    }

    /** @private */
    _selectFocusHistoryGridRow($event: FocusHistorySelectRowEvent): void {
        const { gridTab, rowId } = $event;

        switch (gridTab) {
            case FocusHistoryNavigationRefs.ACTIVE_FOR_PERFORMER:
                return useDispatcher(
                    this,
                    focusSelectorSelectActiveForPerformerRow({ rowId }),
                );
            case FocusHistoryNavigationRefs.RECENTLY_CLOSED_FOR_PERFORMER:
                return useDispatcher(
                    this,
                    focusSelectorSelectClosedForPerformerRow({ rowId }),
                );
            case FocusHistoryNavigationRefs.ACTIVE_FOR_TEAM:
                return useDispatcher(
                    this,
                    focusSelectorSelectActiveForTeamRow({ rowId }),
                );
            case FocusHistoryNavigationRefs.RECENTLY_CLOSED_FOR_TEAM:
                return useDispatcher(
                    this,
                    focusSelectorSelectClosedForTeamRow({ rowId }),
                );
        }
    }

    /** @private */
    _sortFocusHistoryGrid($event: FocusHistorySortGridEvent): void {
        const { gridTab, gridSortEvent } = $event;

        switch (gridTab) {
            case FocusHistoryNavigationRefs.ACTIVE_FOR_PERFORMER:
                return useDispatcher(
                    this,
                    focusSelectorSortActiveForPerformerGrid({
                        column: gridSortEvent.column,
                    }),
                );
            case FocusHistoryNavigationRefs.RECENTLY_CLOSED_FOR_PERFORMER:
                return useDispatcher(
                    this,
                    focusSelectorSortClosedForPerformerGrid({
                        column: gridSortEvent.column,
                    }),
                );
            case FocusHistoryNavigationRefs.ACTIVE_FOR_TEAM:
                return useDispatcher(
                    this,
                    focusSelectorSortActiveForTeamGrid({
                        column: gridSortEvent.column,
                    }),
                );
            case FocusHistoryNavigationRefs.RECENTLY_CLOSED_FOR_TEAM:
                return useDispatcher(
                    this,
                    focusSelectorSortClosedForTeamGrid({
                        column: gridSortEvent.column,
                    }),
                );
        }
    }

    /** @private */
    _focusHistoryClearGrid($event: FocusHistoryNavigationRefs): void {
        switch ($event) {
            case FocusHistoryNavigationRefs.ACTIVE_FOR_PERFORMER:
                return useDispatcher(
                    this,
                    focusSelectorSelectActiveForPerformerRow({ rowId: null }),
                );
            case FocusHistoryNavigationRefs.RECENTLY_CLOSED_FOR_PERFORMER:
                return useDispatcher(
                    this,
                    focusSelectorSelectClosedForPerformerRow({ rowId: null }),
                );
            case FocusHistoryNavigationRefs.ACTIVE_FOR_TEAM:
                return useDispatcher(
                    this,
                    focusSelectorSelectActiveForTeamRow({ rowId: null }),
                );
            case FocusHistoryNavigationRefs.RECENTLY_CLOSED_FOR_TEAM:
                return useDispatcher(
                    this,
                    focusSelectorSelectClosedForTeamRow({ rowId: null }),
                );
        }
    }

    /** @private */
    _selectFocus($event: FocusSelectorSelectFocusEvent): void {
        if (
            this.state.triggerSource ===
                FocusSelectorTriggerSource.COMMITMENT &&
            $event.activeCommitmentId != null
        ) {
            const data: CmpsConfirmDialogOptions = {
                title: this._translateService.instant(
                    TranslationKeyMap._9479_CONFIRMATION,
                ),
                message: this._translateService.instant(
                    TranslationKeyMap._11111_CONFIRM_DIALOG_THERE_IS_ALREADY_AN_ACTIVE_COMMITMENT_WITH_THIS_FOCUS,
                ),
                actions: [
                    {
                        label: this._translateService.instant(
                            TranslationKeyMap._637_OKAY,
                        ),
                        data: 1,
                    },
                    {
                        label: this._translateService.instant(
                            TranslationKeyMap._121_CANCEL,
                        ),
                        data: 0,
                    },
                ],
            };

            const ref = this._dialog.open(CmpsConfirmDialogComponent, { data });

            ref.result.pipe(takeUntilDestroy(this)).subscribe((value) => {
                if (value === 1) {
                    this._goToActiveCommitment($event.activeCommitmentId);
                }
            });
        } else {
            if (
                this.state.triggerSource ===
                FocusSelectorTriggerSource.OBSERVATION
            ) {
                return this._handleCanSubmitFocus($event);
            }

            useDispatcher(this, focusSelectorSelectFocus({ focus: $event }));

            if (this.state.activeMetric?.id == null) {
                this._activeTab = NavigationTabRefs.METRIC_SELECTION;
            } else {
                this._activeTab =
                    this.state.triggerSource ===
                    FocusSelectorTriggerSource.COMMITMENT
                        ? NavigationTabRefs.LOAD_GOALS
                        : this._activeTab;
            }
        }
    }

    /** @private */ _selectGoalRow($event: FocusSelectorSelectGoalRow): void {
        useDispatcher(this, focusSelectorSelectGoalRow($event));
    }

    /** @private */ _deleteSelectedGoal(
        $event: FocusSelectorGoalModel['metricId'],
    ): void {
        useDispatcher(
            this,
            focusSelectorSelectGoalRow({
                rowAndMetricId: $event,
                projectionEvaluationColumn: null,
            }),
        );
    }

    /** @private */ _updateSelectedGoal(
        $event: FocusSelectorUpdateSelectedGoal,
    ): void {
        useDispatcher(this, focusSelectorUpdateSelectedGoal($event));
    }

    /** @private */ _focusSelectorConfirmSelection(): void {
        if (
            this.state.triggerSource !== FocusSelectorTriggerSource.OBSERVATION
        ) {
            if (this.state.loadGoalsGrid?.selection?.goals?.length !== 0) {
                this._handleCanSubmitFocus();
                return;
            }

            const data: CmpsConfirmDialogOptions = {
                title: this._translateService.instant(
                    TranslationKeyMap._9479_CONFIRMATION,
                ),
                message: this._translateService.instant(
                    TranslationKeyMap._11117_CONFIRM_DIALOG_CONFIRM_THIS_FOCUS_WITHOUT_ADDING_GOALS,
                ),
                actions: [
                    {
                        label: this._translateService.instant(
                            TranslationKeyMap._9297_YES,
                        ),
                        data: 1,
                    },
                    {
                        label: this._translateService.instant(
                            TranslationKeyMap._9298_NO,
                        ),
                        data: 0,
                    },
                ],
            };

            const ref = this._dialog.open(CmpsConfirmDialogComponent, { data });

            ref.result.pipe(takeUntilDestroy(this)).subscribe((value) => {
                if (value === 1) {
                    this._handleCanSubmitFocus();
                }
            });
        } else {
            this._handleCanSubmitFocus(this.state.selectedFocus);
        }
    }

    /** @private */ _handleCanSubmitFocus(
        observationSelectedFocus: FocusSelectorFocusModel = null,
    ): void {
        if (this._options.canSelectFocus == null) {
            this._submitFocusSelectorResults(observationSelectedFocus);
        } else {
            const { selected } = getFocusSelectorResult(
                this._options.commitmentId,
                this.state,
                observationSelectedFocus,
            );

            const canSelectFocusRet = this._options.canSelectFocus(selected);

            if (canSelectFocusRet instanceof Promise) {
                void canSelectFocusRet.then((canSelectFocus) => {
                    if (canSelectFocus) {
                        this._submitFocusSelectorResults(
                            observationSelectedFocus,
                        );
                    }
                });
            } else {
                if (canSelectFocusRet) {
                    this._submitFocusSelectorResults(observationSelectedFocus);
                }
            }
        }
    }

    /** @private */ _submitFocusSelectorResults(
        observationSelectedFocus: FocusSelectorFocusModel = null,
    ): void {
        const {
            selected,
            commitmentId,
            activeFocusesForPerformer,
            selectedActiveMetricIds,
            commitmentKpis,
        } = getFocusSelectorResult(
            this._options.commitmentId,
            this.state,
            observationSelectedFocus,
        );

        this._dialogRef.close({
            selected,
            commitmentId,
            activeCommitmentFocuses: null,
            activeFocusesForPerformer,
            selectedActiveMetricIds,
            commitmentKpis,
        });
    }

    /** @private */
    _onScrollToGoal($event: FocusSelectorGoalModel['metricId']): void {
        if (
            this.state.triggerSource === FocusSelectorTriggerSource.COMMITMENT
        ) {
            if (this._activeTab !== NavigationTabRefs.LOAD_GOALS) {
                this._activeTab = NavigationTabRefs.LOAD_GOALS;
            }

            of(null)
                .pipe(
                    delay(0),
                    tap(() => {
                        this._el.nativeElement
                            .querySelector(`[data-goal-id="${$event}"]`)
                            ?.scrollIntoView();
                    }),
                )
                .subscribe();
        }
    }

    private _goToActiveCommitment(
        commitmentId: FocusSelectorSelectFocusEvent['activeCommitmentId'],
    ): void {
        void navigateToExistingCommitment(
            this._router,
            this._activatedRoute,
            commitmentId,
            undefined,
            undefined,
            undefined,
            false,
        );
    }
}
